import { Fragment, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button, Checkbox, Flex, Form, Input, Spin } from 'antd';
import { registEmail } from '@/features/homepage/api/ApiRegistEmail';
import { getUserInfo } from '@/features/homepage/api/ApiGetUserInfo';
import storage from '@/utils/storage';
import { ERR_MESSAGE, COMPANY_CODE, REGEX_EMAIL, RESPONSE_ERR } from "@/constant";
import logoCCBJI from "@/assets/img/2800_CCBJI_Logo.bmp";
import logoFVJ from "@/assets/img/2801_FVJ_Logo.bmp";
import { LoadingSpinner } from '@/components/SpinnerOverlay';
import { ApiGetPrivacyPolicy } from '@/features/homepage/api/ApiGetTermAndPolicyInfo';

// SCN03 

const RegistEmailPage = () => {
  const location = useLocation();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [email, setEmail] = useState('');
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [vkorg, setVkorg] = useState(logoCCBJI);
  const [systemError, setSystemError] = useState('');
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const inputRefInput = useRef(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [isCheckedPolicy, setIsCheckedPolicy] = useState<boolean>(false);
  const [policyText, setPolicyText] = useState('');

  // Fetch user info and privacy policy when page loads
  useEffect(() => {
    const fetchData = async () => {
      // Pull login_id and password from navigation state
      const dataInput = {
        login_id: location.state?.id,
        password: location.state?.dealer_cd,
      };

      // If missing, redirect to login
      if (!dataInput.login_id || !dataInput.password) {
        navigate("/login");
      }

      try {
        // Get user info (name, vkorg, etc.)
        const response = await getUserInfo(dataInput);
        // Get privacy policy text
        const responsePolicyText = await ApiGetPrivacyPolicy(dataInput);

        if (response.data) {
          setName(response.data.name);
          setVkorg(response.data.vkorg);
          if (response.data.vkorg === COMPANY_CODE.FVJ) {
            setVkorg(logoFVJ);
          } else {
            setVkorg(logoCCBJI);
          }
        }
        const company_code = response.data.vkorg;

        // Show privacy policy text
        if (responsePolicyText) {
          const policy_common = responsePolicyText.find(x => x.policy_cd == "1" && x.VKORG == company_code) ? responsePolicyText.find(x => x.policy_cd == "1" && x.VKORG == company_code) : responsePolicyText.find(x => x.policy_cd == "1" && x.VKORG == COMPANY_CODE.CCBJI);
          
          setPolicyText(policy_common ? policy_common.html_content : "")
        }

      } catch (error) {
        // Handle errors for all known error messages
        if (error.response.data === ERR_MESSAGE.WAI001_MESSAGE) {
          setSystemError(ERR_MESSAGE.WAI001_MESSAGE);
        } else if (error.response.data === ERR_MESSAGE.WAI002_MESSAGE) {
          setSystemError(ERR_MESSAGE.WAI002_MESSAGE);
        } else if (error.response.data === ERR_MESSAGE.WAI003_MESSAGE) {
          setSystemError(ERR_MESSAGE.WAI003_MESSAGE);
        } else if (error.response.data === ERR_MESSAGE.WAS001_MESSAGE) {
          setSystemError(ERR_MESSAGE.WAS001_MESSAGE);
        } else if (error.response.data === ERR_MESSAGE.WAS002_MESSAGE) {
          setSystemError(ERR_MESSAGE.WAS002_MESSAGE);
        } else if (error.response.data === ERR_MESSAGE.WAS003_MESSAGE) {
          setSystemError(ERR_MESSAGE.WAS003_MESSAGE);
        } else {
          setSystemError(ERR_MESSAGE.WAS001_MESSAGE);
        }
      } finally {
        setLoading(false);
        focusID();
      }

    }

    fetchData();

  }, [location.state?.dealer_cd, location.state?.id, navigate]);

  // Focus on email input field after loading
  const focusID = () => {
    if (inputRefInput.current) {
      inputRefInput.current.focus();
    }
  }

  // If user navigates to this page incorrectly, redirect to login
  if (location.pathname !== '/regist-email') {
    return <Navigate to="/login" replace />;
  }

  // Handler for back button
  const backToLogin = () => {
    navigate('/login');
  }

  // Handle form submit for registering email
  const handleSubmit = async (values: any) => {
    const input = {
      password: location.state?.dealer_cd,
      login_id: location.state?.id,
      email: values.email.trim().toLowerCase(),
    };

    // If missing required info, do nothing
    if (!input.password || !input.login_id || !input.email) {
      return
    }

    setLoading(true);
    try {
      // Send API request to register email
      const response = await registEmail(input);
      if (response) {
        setShowSuccessModal(true);
        navigate("/regist-email-inform", { state: { showSuccessRegistEmailModal: true, showVkog: vkorg } });
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      if (error.response.data.error_code === RESPONSE_ERR.WAS001) {
        setSystemError(ERR_MESSAGE.WAS001_MESSAGE);
      } else if (error.response.data.code_code === RESPONSE_ERR.WAS002) {
        setSystemError(ERR_MESSAGE.WAS002_MESSAGE);
      }
    } finally {
      // Clear all user info after submission
      storage.clearAll();
      setLoading(false);
    }

  };

  // Show spinner during loading
  if (loading == true) {
    return <LoadingSpinner />;
  }

  // Enable/disable submit button based on policy agreement
  const isEnableddButton = () => {
    if (isCheckedPolicy) {
      return false;
    }
    return true;
  }

  // UI Render
  return (
    <div style={{ display: loading ? 'none' : 'block' }}>
      <div style={{ display: loading ? 'none' : 'block' }}>
        <nav className="p-0 navbar navbar-expand-lg navbar-light justify-content-start bg-white" >
          <ul className="navbar-nav ms-auto">
            <li className="nav-item d-flex justify-content-around" onClick={backToLogin} style={{ height: '71px' }}>
              <img className="m-1" src={vkorg} alt="Logo 1" style={{ height: '90%' }} />
            </li>
          </ul>
        </nav >
      </div>
      {!showSuccessModal && (
        <div style={{ display: loading ? 'none' : 'block' }}>
          <Fragment>
            <div className="container-fluid background-cover-external">
              <ol className="breadcrumb">
                <li className="breadcrumb-item active">初期アドレス登録</li>
              </ol>
              <div className="jumbotron">
                <div className="section">
                  <div className="card-body " style={{ minHeight: '30vh' }}>
                    <Form
                      name="login"
                      layout="vertical"
                      onFinish={handleSubmit}
                      size="large"
                      validateTrigger="onSubmit"
                    >
                      <ul className="list-group list-group-flush">
                        <li className="list-group-item">
                          <span className='text-coca'>{systemError}</span>
                        </li>
                        <li className="list-group-item">
                          {/*eslint-disable-next-line no-irregular-whitespace */}
                          <span><b>{name}</b>　様、このたびは弊社の登録サイトにアクセスいただき、誠にありがとうございます。</span><br />
                          <p>下記にメールアドレスを入力してください。</p>
                          <Flex vertical gap={12}>
                            <Form.Item
                              name="email"
                              rules={[
                                { required: true, message: "メールアドレスが入力必須" },
                                {
                                  validator: (_, value) => {
                                    if (!value) { return Promise.resolve() };

                                    const domainRegex = REGEX_EMAIL;
                                    const latinRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

                                    // eslint-disable-next-line no-control-regex

                                    if (!domainRegex.test(value)) {
                                      return Promise.reject(ERR_MESSAGE.MESSAGE_ERROR_VALIDATE_EMAIL_REGEX);
                                    }
                                    if (!latinRegex.test(value)) {
                                      return Promise.reject(ERR_MESSAGE.MESSAGE_ERROR_VALIDATE_EMAIL_REGEX);
                                    }


                                    return Promise.resolve();
                                  }
                                }
                              ]}
                            >
                              <Input ref={inputRefInput} />
                            </Form.Item>
                          </Flex>
                          <p>利用規約をご確認のうえ、ご同意頂ける場合は「同意しました」にチェックを入れ確認を押してください。</p>
                        </li>
                        <div className="list-group-item">
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ width: "30%" }}>サービス利用規約</span>
                            <span style={{ width: "45%" }}>一番下までスクロールしてください</span>
                          </div>
                          <div style={{ fontSize: "13px", border: '1px solid #222', }}>
                            <div
                              ref={scrollRef}
                              // onScroll={handleScroll}
                              style={{
                                height: '20rem',
                                overflowY: 'auto',
                                padding: 8,
                                background: '#fff',
                                marginBottom: 16,
                                whiteSpace: 'pre-line',
                                fontSize: 15
                              }}
                            >
                              <div className="policyTextCommon" dangerouslySetInnerHTML={{ __html: policyText }}></div>
                              <div style={{ alignItems: 'center', marginBottom: "1rem", textAlign: "center" }}>
                                <Checkbox
                                  checked={isCheckedPolicy}
                                  onChange={e => setIsCheckedPolicy(e.target.checked)}
                                >
                                  同意しました
                                </Checkbox>
                              </div>
                            </div>
                          </div>
                        </div>
                      </ul>
                      {/* Submit and back buttons */}
                      <li className="list-group-item">
                        <div className="d-flex justify-content-center">
                          <Button className="btn bg-discard col-md-3 col-sm-6 m-2" onClick={backToLogin} >
                            <span className='text-white' style={{ fontSize: '14px' }}  >戻る</span>
                          </Button>
                          <Button htmlType="submit"
                            className="btn bg-coca col-md-3 col-sm-6 m-2"
                            disabled={isEnableddButton()}
                            style={{ backgroundColor: isEnableddButton() ? '#d9363e' : '' }}
                          >
                            <span className="text-white" style={{ fontSize: '14px' }}  >確認 </span>
                          </Button>
                        </div>
                      </li>
                    </Form>
                  </div>

                </div>
              </div>
            </div>
          </Fragment>
        </div>
      )}
    </div >
  );
};

export default RegistEmailPage;